<template>
  <div class="flex flex-col">
    <div v-if="picklistOptions !== undefined" :style="styleObject">
      <VueDraggableNext
        v-model="localPicklistOptions"
        handle=".drag-handle"
        class="w-full"
        :style="{ marginTop: field.style === 'bubble' && !isActive ? '6px' : '0px' }"
      >
        <div
          v-for="(pickerOption, index) in localPicklistOptions"
          :key="index"
          :style="minimumWidthRadio"
          class="option-radio"
          :class="{ 'option-radio-bubble': field.style === 'bubble' }"
        >
          <div class="align-together flex items-start">
            <Menu05Icon
              v-if="isActive"
              class="drag-handle ml-0 mr-2 mt-1 h-4 w-4 flex-shrink-0 cursor-move text-gray-500"
            />
            <input
              v-if="type === 'input'"
              :id="field.id + '_' + index"
              type="radio"
              :name="field.id"
              :disabled="disabled"
              class="flex-shrink-0"
              :class="{ 'radio-bubble': field.style === 'bubble' }"
              :value="typeof pickerOption === 'string' ? pickerOption : pickerOption.label"
              @keyup="(e) => $emit('keyup', e)"
              @change="(e) => $emit('change', e)"
            />
            <label
              :id="`option-label-${field.id}-${index}`"
              :ref="(el) => setLabelRef(el as HTMLElement, index)"
              :for="null"
              contenteditable="true"
              :style="[
                field.style === 'bubble' ? styleObject : undefined,
                labelInlineStyle,
                syncedLabelInlineStyle,
              ]"
              class="inline-block w-auto flex-grow cursor-text pr-2 outline-none"
              :class="{ 'label-bubble bubble-container bubble-label': field.style === 'bubble' }"
              @beforeinput="measureAndSyncLabelWidths()"
              @input="
                (e) => {
                  updateOptionLabel(e, index);
                  handleSafariCursor(e);
                  measureAndSyncLabelWidths();
                }
              "
              @compositionend="measureAndSyncLabelWidths()"
              @paste="() => measureAndSyncLabelWidths()"
              @keydown="(e) => e.key !== 'Tab' && handleSafariCursor(e)"
              @keydown.enter.prevent.stop="handleEnterOnOption(index)"
              @keydown.capture="
                (e) => handleOptionTabNavigation(e, index, localPicklistOptions.length, getLabelRef)
              "
              @keyup.enter.prevent.stop
              @keyup.tab.prevent.stop
              @keyup.capture="suppressTabKeyUp"
              @focus="handleFocus"
              @keyup="
                (e) => {
                  handleEditing(e, index);
                  measureAndSyncLabelWidths();
                }
              "
            >
              {{ typeof pickerOption === "string" ? pickerOption : pickerOption.label }}
            </label>
            <Trash01Icon
              v-if="isActive && !(field?.model && field.model.startsWith('custom_objects'))"
              class="ml-2 mt-1 h-4 w-4 flex-shrink-0 cursor-pointer text-gray-500"
              @click="prepareToRemoveOption(index)"
            />
          </div>
        </div>
      </VueDraggableNext>
    </div>
    <div
      v-if="picklistOptionsImage !== undefined"
      :class="[
        'row',
        picklistOptions === undefined || picklistOptionsImage.length > 0
          ? 'container-group-image'
          : '',
      ]"
      :style="styleObject"
      class="flex w-full flex-row flex-wrap items-center justify-start gap-4"
    >
      <div
        v-for="(pickerOption, index) in picklistOptionsImage"
        :key="index"
        :style="minimumWidthRadioImage"
        :class="[
          picklistOptions === undefined && disabled ? 'option-radio col-md-3' : 'option-radio',
        ]"
        class="h-[95px] w-[100px] flex-none"
      >
        <div
          v-if="pickerOption"
          :class="[
            disabled ? 'radio-image-group' : 'radio-field-group',
            pickerOption.src ? 'radio-image-option-set' : '',
          ]"
        >
          <input
            v-if="type === 'input'"
            :id="field.id + '_' + index"
            type="radio"
            :disabled="disabled"
          />

          <!-- For Image as radio button : If its disable mode (For Drag and Drop) -->
          <div v-if="disabled" class="opt-container-img-label">
            <label :for="field.id + '_' + index">
              <img
                v-if="pickerOption.src"
                class="radio-image-option-set-image"
                :src="pickerOption.src"
                :alt="t('common.optionImageAlt')"
              />
            </label>
            <div class="img-opt-title">{{ pickerOption.label || "Option" }}</div>
          </div>

          <!-- For Image as radio button : If its not disable mode (For contact section) -->
          <label v-else-if="pickerOption.src && !disabled" :for="field.id + '_' + index">
            <img
              class="radio-image-option-set-image"
              :src="pickerOption.src"
              :alt="t('common.optionImageAlt')"
            />
            <span>{{ pickerOption.label || "Option" }}</span>
          </label>
        </div>
      </div>
    </div>
    <div v-if="isActive && !picklistOptionsImage.length" class="mt-[-1rem] flex items-center">
      <UIButton id="add-option" text class="mt-4" :disabled="isLastOptionEmpty" @click="addOption">
        <PlusIcon class="mr-1.5 h-5 w-5" />
        {{ t("common.addOption") }}
      </UIButton>
    </div>
    <template v-if="isActive">
      <div
        ref="shortLabelElement"
        contenteditable="true"
        class="short-label font-inter cursor-text border-0 bg-transparent p-0 text-sm shadow-none focus:outline-none"
        :data-placeholder="t('common.shortLabelPlaceholder')"
        @blur="updateShortLabel"
        @input="handleShortLabelInput"
        @keydown="handleSafariCursor"
        @click="(e) => e.stopPropagation()"
        @focus="(e) => e.stopPropagation()"
        v-text="shortLabel"
      ></div>
    </template>
    <template v-else>
      <span v-if="field.shortLabel" class="short-label font-inter break-words text-sm">
        {{ field.shortLabel }}
      </span>
    </template>
  </div>
  <DeleteConfirmationModal
    :show="showDeleteModal"
    @confirm="removeOption"
    @cancel="cancelRemoveOption"
  />
</template>
<script setup lang="ts">
import { CONSTANTS } from "@/util/constants";
import {
  fixSafariCursor,
  getCaretOffsetWithin,
  handleOptionTabNavigation,
  handleShortLabelInput as handleShortLabelInputUtil,
  setCaretOffsetWithin,
  suppressTabKeyUp,
} from "@/util/methods";
import { Menu05Icon, PlusIcon, Trash01Icon } from "@gohighlevel/ghl-icons/24/outline";
import { UIButton } from "@gohighlevel/ghl-ui";
import { isEqual } from "lodash";
import { computed, CSSProperties, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { VueDraggableNext } from "vue-draggable-next";
import { useI18n } from "vue-i18n";
import DeleteConfirmationModal from "./DeleteConfirmationModal.vue";

const { t } = useI18n();

interface PickerOption {
  src?: string;
  label?: string;
  [key: string]: any;
}

const props = defineProps({
  type: {
    type: String,
    default: "input",
  },
  name: {
    type: String,
    default: "",
  },
  value: {
    type: String,
    default: "",
  },
  currentLocationId: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "",
  },
  customClass: {
    type: String,
    default: "",
  },
  modelValue: {
    type: Array,
    default: () => [],
  },
  picklistOptions: {
    type: Array as () => (string | PickerOption)[],
    default: () => [],
  },
  picklistOptionsImage: {
    type: Array as () => PickerOption[],
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  field: {} as any,
  layout: {
    type: Number,
    default: 1,
  },
  styleObject: {
    type: Object as () => CSSProperties,
    default: () => ({}),
  },
  isActive: {
    type: Boolean,
    default: false,
  },
  customFieldCreated: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "update:shortLabel",
  "update:modelValue",
  "update:picklistOptions",
  "keyup",
  "change",
]);

const shortLabel = ref(props.field.shortLabel || "");

// Add local state for the draggable component
const localPicklistOptions = ref([...props.picklistOptions]);

// Add bidirectional watchers with flag to prevent recursive updates
const isInternalUpdate = ref(false);

watch(
  () => props.picklistOptions,
  (newVal) => {
    if (!isInternalUpdate.value && !isEqual(newVal, localPicklistOptions.value)) {
      localPicklistOptions.value = JSON.parse(JSON.stringify(newVal));
    }
    isInternalUpdate.value = false;
  },
  { deep: true, immediate: true }
);

watch(
  () => localPicklistOptions.value,
  (newVal) => {
    if (JSON.stringify(newVal) !== JSON.stringify(props.picklistOptions)) {
      isInternalUpdate.value = true;
      emit("update:picklistOptions", JSON.parse(JSON.stringify(newVal)));
    }
  },
  { deep: true }
);

watch(
  () => props.field.shortLabel,
  (newVal) => {
    shortLabel.value = newVal || "";
  }
);

const leftPosition = computed(() => {
  // Keep the standard style's radio bullet to the right of the drag handle
  // when active. Bubble style manages its own marker via different rules.
  if (props.field?.style === "bubble") {
    return "0";
  }
  return props.isActive ? "28px" : "0";
});
const labelInlineStyle = computed((): CSSProperties => {
  // For bubble style, avoid extra left padding so the
  // drag handle aligns visually with the label container
  if (props.field?.style === "bubble") {
    return {} as CSSProperties;
  }
  // Standard style: keep original spacing so pseudo elements stay correct
  return { paddingLeft: "25px" } as CSSProperties;
});
// Keep bubble labels width in sync with the widest label
const labelRefs = ref<HTMLElement[]>([]);
const setLabelRef = (el: HTMLElement | null, index: number): void => {
  if (el) {
    labelRefs.value[index] = el;
  }
};
const getLabelRef = (i: number): HTMLElement | null => labelRefs.value[i] || null;
const syncedLabelWidth = ref<number>(0);
const measureAndSyncLabelWidths = (): void => {
  // Phase 1: clear synced width so labels can shrink to their natural size
  const previousSynced = syncedLabelWidth.value;
  if (previousSynced !== 0) {
    syncedLabelWidth.value = 0;
  }
  nextTick(() => {
    let maxWidth = 0;
    for (const el of labelRefs.value) {
      if (!el) {
        continue;
      }
      // Temporarily remove inline minWidth to get natural content width
      const prevMinWidth = el.style.minWidth;
      el.style.minWidth = "0px";
      // Use scrollWidth to capture content + padding size
      const width = Math.ceil(el.scrollWidth);
      // Restore prior minWidth
      el.style.minWidth = prevMinWidth;
      if (width > maxWidth) {
        maxWidth = width;
      }
    }
    syncedLabelWidth.value = maxWidth;
  });
};
onMounted(() => {
  measureAndSyncLabelWidths();
  window.addEventListener("resize", measureAndSyncLabelWidths);
});
onUnmounted(() => {
  window.removeEventListener("resize", measureAndSyncLabelWidths);
});
watch(
  () => localPicklistOptions.value,
  () => {
    measureAndSyncLabelWidths();
  },
  { deep: true }
);
watch(
  () => props.field?.style,
  () => {
    measureAndSyncLabelWidths();
  }
);
const syncedLabelInlineStyle = computed((): CSSProperties => {
  if (props.field?.style === "bubble") {
    const BASE_MIN = 82; // px
    const targetMin = Math.max(BASE_MIN, syncedLabelWidth.value || 0);
    return { minWidth: `${targetMin}px`, maxWidth: "100%" } as CSSProperties;
  }
  return {} as CSSProperties;
});
const getWidth = () => {
  let width = 100;
  if (props.layout === CONSTANTS.TWO_COLUMN) {
    width = Math.max(width / props.field.columnsNumber, 50);
  } else {
    width = Math.max(width / props.field.columnsNumber, 20);
  }
  if (props.field.spreadColumns) {
    return width + "%";
  }
  return "100%";
};

const minimumWidthRadio = computed((): CSSProperties => {
  const width = getWidth();
  return {
    position: "relative",
    display: "inline-block",
    width,
  };
});
const minimumWidthRadioImage = computed((): CSSProperties => {
  const width = getWidth();
  return {
    width: props.field.spreadColumns ? width : undefined,
  };
});

const updateOptionLabel = (event: Event, index: number) => {
  const target = event.target as HTMLElement;
  const text = target.innerText.replace(/\u200B/g, "");
  const caret = getCaretOffsetWithin(target);

  if (typeof localPicklistOptions.value[index] === "string") {
    (localPicklistOptions.value as (string | PickerOption)[])[index] = text;
  } else {
    (localPicklistOptions.value[index] as PickerOption).label = text;
  }

  nextTick(() => {
    const el = labelRefs.value[index] || target;
    setCaretOffsetWithin(el, caret);
  });
};

const indexToRemove = ref<number | null>(null);
const showDeleteModal = ref(false);

const prepareToRemoveOption = (index: number) => {
  if (props.picklistOptions.length > 1) {
    if (props.customFieldCreated) {
      indexToRemove.value = index;
      showDeleteModal.value = true;
    } else {
      const newOptions = [...localPicklistOptions.value];
      newOptions.splice(index, 1);
      localPicklistOptions.value = newOptions;
    }
  }
};

const removeOption = () => {
  if (indexToRemove.value !== null) {
    const newOptions = [...localPicklistOptions.value];
    newOptions.splice(indexToRemove.value, 1);
    localPicklistOptions.value = newOptions;
  }
  showDeleteModal.value = false;
};

const cancelRemoveOption = () => {
  indexToRemove.value = null;
  showDeleteModal.value = false;
};

const isLastOptionEmpty = computed(() => {
  const lastOption = props.picklistOptions[props.picklistOptions.length - 1];
  if (typeof lastOption === "string") {
    return lastOption.trim() === "";
  }
  return lastOption?.label?.trim() === "";
});

const addOption = () => {
  addOptionAt((localPicklistOptions.value?.length ?? 1) - 1);
};

const addOptionAt = (index: number) => {
  const newOptions = [...localPicklistOptions.value];
  const insertAt = Math.min(Math.max(index + 1, 0), newOptions.length);
  const newId = `option-${Date.now()}`;
  if (typeof newOptions[0] === "string") {
    newOptions.splice(insertAt, 0, t("common.typeAnOption"));
  } else {
    newOptions.splice(insertAt, 0, { id: newId, label: t("common.typeAnOption") });
  }
  localPicklistOptions.value = newOptions;
  nextTick(() => {
    const resolvedLabel =
      labelRefs.value[insertAt] ||
      (document.getElementById(`option-label-${props.field.id}-${insertAt}`) as HTMLElement | null);
    if (resolvedLabel) {
      resolvedLabel.focus();
      const range = document.createRange();
      range.selectNodeContents(resolvedLabel);
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  });
};

const handleFocus = (event: FocusEvent) => {
  const target = event.target as HTMLElement;

  const selection = window.getSelection();
  if (selection) {
    selection.removeAllRanges();
  }

  const sanitized = (target.textContent || "").replace(/\u200B/g, "").trim();
  if (!sanitized) {
    let textNode = target.firstChild as Text | null;
    if (!textNode || textNode.nodeType !== Node.TEXT_NODE) {
      target.textContent = "";
      textNode = document.createTextNode("\u200B");
      target.appendChild(textNode);
    }
    if (!textNode.textContent) {
      textNode.textContent = "\u200B";
    }
    const range = document.createRange();
    range.setStart(textNode, 0);
    range.collapse(true);
    selection?.addRange(range);
    return;
  }

  const range = document.createRange();
  range.selectNodeContents(target);
  selection?.addRange(range);
};

const shortLabelElement = ref<HTMLElement | null>(null);

const updateShortLabel = () => emit("update:shortLabel", shortLabel.value);

const handleShortLabelInput = (e: Event) =>
  handleShortLabelInputUtil(e, shortLabel, updateShortLabel);

const handleEditing = (event: KeyboardEvent, index: number) => {
  const target = event.target as HTMLElement;

  if (target.textContent?.trim() === "") {
    if (target.textContent) {
      target.textContent = "";

      const range = document.createRange();
      range.setStart(target, 0);
      range.collapse(true);

      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  }
};

const handleEnterOnOption = (index: number) => {
  addOptionAt(index);
};

const handleSafariCursor = (e: Event) => {
  const isKeyDown = e.type === "keydown";
  fixSafariCursor(e, isKeyDown);
};
</script>
<style>
[type="radio"]:checked + img {
  outline: 1px solid #f00;
  box-shadow: 0px 1px 8px 2px #000;
}

/* Radio image options styling */
.radio-image-option-set-image {
  max-width: 100%;
  max-height: 65px; /* Limit height to leave space for title */
  width: auto;
  height: auto;
  object-fit: contain; /* Maintain aspect ratio without stretching */
  border-radius: 4px 4px 0 0;
}

.container-group-image .radio-image-option-set label:before,
.radio-image-option-set label:before {
  opacity: 0;
}
.container-group-image .radio-image-option-set label,
.radio-image-option-set label {
  padding: 0;
  display: block;
  width: 100%;
  height: 65px; /* Fixed height for image container */
}
.container-group-image .radio-image-option-set,
.radio-image-option-set {
  margin: 0 0 8px 0; /* Add bottom margin for spacing between rows when wrapped */
  border: 0.15rem solid #787f8221;
  padding: 0;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100px;
  height: 95px;
}
.container-group-image .radio-image-option-set .opt-container-img-label,
.radio-image-option-set .opt-container-img-label {
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
}
.container-group-image .radio-image-option-set .opt-container-img-label label,
.radio-image-option-set .opt-container-img-label label {
  flex: 0 0 65px; /* Fixed height for the label */
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.container-group-image .radio-image-option-set label img,
.radio-image-option-set label img {
  max-width: 100%;
  max-height: 100%;
  margin: 0 auto;
  display: block;
}
.container-group-image .img-opt-title,
.img-opt-title {
  background: #444444;
  color: #fff;
  text-align: center;
  text-transform: capitalize;
  font-weight: 800;
  border-radius: 0 0 4px 4px;
  padding: 5px;
  width: 100%;
  height: 30px; /* Fixed height for title */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 10px; /* Smaller font size to fit in the limited space */
}
.menu-field-wrap.col-sm-6 .option-radio.col-md-3 {
  max-width: 50%;
}

.tw-bottom-input,
.tw-bottom-input:focus {
  --tw-ring-color: none;
  --tw-ring-shadow: none;
  border: none;
  border-bottom: 1px solid rgba(209, 213, 219, var(--tw-border-opacity));
}

/*Radio Button*/
.option-radio {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  position: relative;
}

.option-radio label {
  line-height: 30px;
  display: block;
  padding-left: 25px;
  margin-bottom: 0;
  font-size: 0.875rem;
}

.option-radio label:before {
  content: "";
  display: block;
  width: 15px;
  height: 15px;
  border-radius: 10px;
  border: 1px solid #8b8b8b;
  background: #fff;
  position: absolute;
  top: 5px;
  left: v-bind(leftPosition);
}

.option-radio label:after {
  font-family: "Magicons";
  color: #fff;
  content: "\e96c";
  font-size: 0.625rem;
  text-align: center;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 1px solid #00d26d;
  background-color: #00d26d;
  background-size: 10px 7px;
  position: absolute;
  top: 10px;
  left: 5px;
  -webkit-transition: all 0.2s ease-in-out 0s;
  -o-transition: all 0.2s ease-in-out 0s;
  transition: all 0.2s ease-in-out 0s;
  opacity: 0;
}

.option-radio input[type="radio"] {
  margin: 0;
  display: none;
  visibility: hidden;
}

.option-radio input[type="radio"]:checked + label {
  color: #2a3135;
}

.option-radio input[type="radio"]:checked + label:after {
  width: 15px;
  height: 15px;
  opacity: 1;
  top: 5px;
  left: 0;
  border-radius: 10px;
}

.option-radio input[type="radio"] + label:before,
.option-radio input[type="radio"] + label:after {
  border-radius: 50%;
}

.option-radio label {
  width: 100%;
}
.option-radio label input.add-custom-opt {
  background: #fff;
  border: none;
  width: 50%;
  border-bottom: 1px solid #e1e1e1;
  border-radius: 3px;
}

.short-label {
  background-color: transparent;
  margin-top: 4px;
  font-size: 14px;
  color: #4b5563;
}

.short-label:empty:before {
  content: attr(data-placeholder);
}

/* Target all radio inputs to ensure cursor consistency */
input[type="radio"] {
  cursor: text !important;
  pointer-events: auto !important;
}

/* Bubble Style Overrides */
.option-radio-bubble {
  margin-bottom: 8px;
}

.option-radio-bubble label {
  background: transparent;
  border: 1px solid #d0d5dd;
  border-radius: 12px;
  padding: 8px;
  padding-right: 20px;
  margin-bottom: 0;
  /* Add 2px horizontal gap between radio bubbles */
  margin-left: 2px;
  margin-right: 2px;
  display: flex;
  align-items: center;
  width: auto;
  max-width: 100%;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
  cursor: pointer;
}

.option-radio-bubble label:hover {
  background: transparent;
  border-color: #d1d5db;
}

.option-radio-bubble input[type="radio"]:checked + label {
  background: transparent;
  border-color: #3b82f6 !important;
  color: #1e40af;
}

.option-radio-bubble label:before {
  content: "";
  display: block;
  width: 15px;
  height: 15px;
  border-radius: 10px;
  border: 1px solid #8b8b8b;
  background: #fff;
  margin-right: 8px;
  position: relative;
  top: auto;
  left: auto;
  transform: none;
  flex-shrink: 0;
}

.option-radio-bubble input[type="radio"]:checked + label:before {
  border-color: #3b82f6;
  background: #3b82f6;
}

.option-radio-bubble label:after {
  content: "";
  width: 6px;
  height: 6px;
  background: #ffffff;
  border-radius: 50%;
  border: none;
  position: absolute;
  margin-left: -11px;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0;
}

.option-radio-bubble input[type="radio"]:checked + label:after {
  opacity: 1;
}

/* Bubble mode - Center icons vertically */
.option-radio-bubble .align-together {
  align-items: center !important;
}
.option-radio-bubble .drag-handle,
.option-radio-bubble .ml-2.mt-1 {
  margin-top: 0 !important;
  align-self: center;
}

/* Bubble mode - Prevent label from taking full width */
.option-radio-bubble label {
  flex-grow: 0 !important;
  width: auto !important;
}

.option-radio:not(.option-radio-bubble) .align-together {
  align-items: center !important;
}

.option-radio:not(.option-radio-bubble) .drag-handle,
.option-radio:not(.option-radio-bubble) input[type="radio"],
.option-radio:not(.option-radio-bubble) .ml-2.mt-1 {
  margin-top: 0 !important;
}

.option-radio:not(.option-radio-bubble) label:before {
  top: 50% !important;
  transform: translateY(-50%) !important;
}

.option-radio:not(.option-radio-bubble) label:after {
  top: 50% !important;
  transform: translateY(-50%) !important;
  /* Keep inner dot horizontally centered within the outer ring */
  left: calc(v-bind(leftPosition) + 5px) !important;
}

.option-radio:not(.option-radio-bubble) input[type="radio"]:checked + label:after {
  top: 50% !important;
  transform: translateY(-50%) !important;
  /* When checked, inner fill expands to full ring; align left with ring */
  left: v-bind(leftPosition) !important;
}

.option-radio label {
  margin-bottom: 0 !important;
}
</style>
<style scoped>
.flex.flex-col .row > * {
  width: auto !important;
}
</style>
